// src/admin/screens/config/modals/UpdateConfigModal.tsx
// Edits a single system_config row. The modal adapts its editor to the
// row's `kind` (text | int | json) and writes back to the matching column
// only, which keeps the CHECK constraint `exactly_one_value` satisfied.
//
// Validation:
//   - text: non-empty after trim.
//   - int: must parse as a finite integer (JS Number.isSafeInteger).
//   - json: must parse via JSON.parse — textarea shows parse error inline.
//   - reason: required, min 10 chars (same bar as other admin mutations).
//
// Audit payload includes `{ from, to, reason }` so the diff is reconstructable
// from the audit log without having to cross-reference historical rows.
//
// Deviations from the draft plan:
//   - Plan stores `value: jsonb`; migration stores into three typed columns
//     under an "exactly one non-null" CHECK. We route writes per `row.kind`.
//   - Plan explicitly writes `updated_at: new Date().toISOString()`; we let
//     the DB default (`default now()`) handle the timestamp — UPDATE does
//     not re-run the default, but migration 8 uses `default now()` on the
//     `updated_at` column without a trigger, so the existing UI behaviour
//     shipped in other admin mutations (also not setting updated_at) is
//     preserved. A later migration could add a BEFORE UPDATE trigger to
//     stamp it automatically.
//   - The plan sets `updated_by_admin_id` explicitly from the caller; we
//     keep that behaviour so the row actually records who last edited it.
import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import { useAuth } from '@admin/auth/useAuth';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { ConfigRow } from '@admin/hooks/useSystemConfig';
import type { Json } from '@/lib/database.types';
import styles from './UpdateConfigModal.module.css';

interface Props {
  row: ConfigRow;
  onClose: (refresh: boolean) => void;
}

function initialEditorValue(row: ConfigRow): string {
  if (row.kind === 'text') return String(row.value ?? '');
  if (row.kind === 'int') return String(row.value ?? '');
  try {
    return JSON.stringify(row.value, null, 2);
  } catch {
    return String(row.value);
  }
}

type UpdatePatch =
  | { value_text: string; updated_by_admin_id: string }
  | { value_int: number; updated_by_admin_id: string }
  | { value_json: Json; updated_by_admin_id: string };

function buildPatch(
  row: ConfigRow,
  raw: string,
  adminId: string,
): { patch: UpdatePatch; next: unknown } | { error: string } {
  if (row.kind === 'text') {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return { error: 'Value cannot be empty.' };
    return {
      patch: { value_text: trimmed, updated_by_admin_id: adminId },
      next: trimmed,
    };
  }
  if (row.kind === 'int') {
    const n = Number(raw.trim());
    if (!Number.isFinite(n) || !Number.isInteger(n)) {
      return { error: 'Value must be an integer.' };
    }
    if (!Number.isSafeInteger(n)) {
      return { error: 'Value is outside the safe integer range.' };
    }
    return {
      patch: { value_int: n, updated_by_admin_id: adminId },
      next: n,
    };
  }
  // kind === 'json'
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    return { error: `Invalid JSON: ${(err as Error).message}` };
  }
  return {
    patch: { value_json: parsed as Json, updated_by_admin_id: adminId },
    next: parsed,
  };
}

export default function UpdateConfigModal({ row, onClose }: Props) {
  const { admin } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [raw, setRaw] = useState(initialEditorValue(row));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [valueError, setValueError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!admin) return;
    if (reason.trim().length < 10) {
      toast({ kind: 'error', msg: 'Reason must be at least 10 characters.' });
      return;
    }
    const built = buildPatch(row, raw, admin.id);
    if ('error' in built) {
      setValueError(built.error);
      return;
    }
    setValueError(null);
    setBusy(true);
    try {
      const { error } = await supabase
        .from('system_config')
        .update(built.patch)
        .eq('key', row.config_key);
      if (error) throw error;
      await recordAudit({
        action: 'config.update',
        target_type: 'config',
        target_id: row.config_key,
        payload: {
          kind: row.kind,
          from: row.value as unknown,
          to: built.next,
          reason: reason.trim(),
        },
      });
      toast({ kind: 'success', msg: `${row.config_key} updated.` });
      await qc.invalidateQueries({ queryKey: ['system-config'] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  const editorHint =
    row.kind === 'text'
      ? 'Free-form text.'
      : row.kind === 'int'
        ? 'Integer value — use ngwee (100 ngwee = K1) or basis points as described.'
        : 'JSON value. Parsed on save; invalid JSON blocks submission.';

  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={`Edit ${row.config_key}`}
      width={560}
    >
      <form onSubmit={submit} className={styles.form}>
        <p className={styles.lead}>{row.description}</p>
        <FormField
          label={`Value (${row.kind})`}
          htmlFor="uc-value"
          hint={editorHint}
          error={valueError ?? undefined}
          required
        >
          <textarea
            id="uc-value"
            rows={row.kind === 'json' ? 8 : 3}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            className={styles.mono}
          />
        </FormField>
        <FormField
          label="Reason"
          htmlFor="uc-reason"
          hint="Required and audit-logged (min 10 characters)."
          required
        >
          <textarea
            id="uc-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" loading={busy}>
            Save
          </Button>
        </div>
      </form>
    </Modal>
  );
}
